import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircleIcon, DocumentIcon } from '@heroicons/react/24/outline';
import { promotionPackagesApi, paymentApi } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import DealdenLoader from '../components/DealdenLoader';

const PlansPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  const { data: promotionPackages, isLoading: isLoadingPackages } = useQuery({
    queryKey: ['promotion-packages'],
    queryFn: () => promotionPackagesApi.getActive(),
  });

  // Mutation for activating free plans
  const activateFreePlanMutation = useMutation({
    mutationFn: async (packageId: string) => {
      const response = await paymentApi.activateFreePlan(packageId);
      return response.data;
    },
    onSuccess: (data) => {
      toast.success('Free plan activated successfully!');
      // Invalidate user plans to refetch updated data
      queryClient.invalidateQueries({ queryKey: ['user-plans'] });
      // Redirect to create ad page with the activated plan
      navigate(`/post-ad?planId=${data.planId}`);
    },
    onError: () => {
      toast.error('Error activating free plan');
    }
  });

  const handlePlanSelection = async (pkg: any) => {
    if (!user) {
      toast.error('Please log in to your account first');
      navigate('/login');
      return;
    }

    if (pkg.price === 0) {
      // Free plan - activate immediately
      toast.loading('Activating free plan...');
      activateFreePlanMutation.mutate(pkg.id);
    } else {
      // Paid plan - redirect to create ad page for payment processing
      navigate(`/post-ad?packageId=${pkg.id}`);
    }
  };

  if (isLoadingPackages) {
    return (
      <DealdenLoader 
        fullScreen={true}
        size="large" 
        message="Loading plans..." 
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <DocumentIcon className="h-12 w-12 text-primary-600" />
          </div>
          <h1 className="text-4xl font-semibold text-gray-900 mb-4">Promotion Plans</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Choose one of our promotion plans to place your ads in the best positions and get more views
          </p>
        </div>

        {promotionPackages?.data && promotionPackages.data.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {promotionPackages.data.map((pkg) => (
              <div
                key={pkg.id}
                className={`relative bg-white rounded-2xl p-8 shadow-xl border-2 transition-all duration-300 hover:shadow-2xl hover:scale-105 ${
                  pkg.isPopular 
                    ? `border-${pkg.colorClass}-300 bg-gradient-to-br from-${pkg.colorClass}-50 to-white` 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {pkg.isPopular && (
                  <div className={`absolute -top-4 right-6 bg-gradient-to-r from-${pkg.colorClass}-500 to-${pkg.colorClass}-600 text-white px-6 py-2 rounded-full text-sm font-semibold shadow-lg`}>
                    ⭐ Most Popular
                  </div>
                )}
                
                <div className="text-center mb-8">
                  <div className="text-6xl mb-4">{pkg.iconEmoji}</div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">{pkg.name}</h3>
                  <p className="text-gray-600 leading-relaxed">{pkg.description}</p>
                </div>

                <div className="text-center mb-8">
                  <div className="text-4xl font-semibold text-gray-900 mb-2">
                    {pkg.price === 0 ? 'Free' : `$${pkg.price.toFixed(2)}`}
                  </div>
                  <div className="text-lg text-gray-500">
                    {pkg.durationDays} days
                  </div>
                </div>

                <div className="space-y-4 mb-8">
                  {pkg.features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <CheckCircleIcon className={`h-6 w-6 text-${pkg.colorClass}-500 flex-shrink-0`} />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => {
                    handlePlanSelection(pkg);
                  }}
                  className={`w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all duration-200 ${
                    pkg.isPopular
                      ? `bg-gradient-to-r from-${pkg.colorClass}-500 to-${pkg.colorClass}-600 hover:from-${pkg.colorClass}-600 hover:to-${pkg.colorClass}-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105`
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700 hover:text-gray-900'
                  }`}
                >
                  {pkg.price === 0 ? 'Select Free' : 'Buy and Use'}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <DocumentIcon className="h-24 w-24 text-gray-300 mx-auto mb-6" />
            <h3 className="text-2xl font-medium text-gray-900 mb-4">No Plans Available</h3>
            <p className="text-gray-500 text-lg">Currently, no promotion packages are available.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlansPage; 